import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  AnchorType,
  CheckStatus,
  EntityRefUtil,
  formatDateWithUserTimezone,
  formatManuallyWithUserTimezone,
  GetCheckResponseDTO,
  GetScheduleByIdResponseDTO,
  isCheckCompleted,
  parseCalendarInterval,
  resolveFixedCalendarDueDate,
  TagEntity,
  UpdateCheckRequestDTO,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { Controller, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { Link, useNavigate } from 'react-router-dom';
import { components, type OptionProps, type SingleValueProps } from 'react-select';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { AWAFormSubmissionDrawer } from '@/awa-pages/[workspaceId]/form/submission-drawer';
import CheckProgressBar from '@/components/checks/check-progress-bar';
import { SaveChangesDialog } from '@/components/common/save-changes-dialog';
import ActivityIcon from '@/components/icons/activity';
import ArrowLeftIcon from '@/components/icons/arrow-left';
import CheckIcon from '@/components/icons/check';
import ClockIcon from '@/components/icons/clock';
import CloseIcon from '@/components/icons/close';
import TimelineCircleIcon from '@/components/icons/timeline-shapes/circle';
import TimelineDiamondIcon from '@/components/icons/timeline-shapes/diamond';
import TimelineFidgetSpinnerIcon from '@/components/icons/timeline-shapes/fidget-spinner';
import TimelineHexagonIcon from '@/components/icons/timeline-shapes/hexagon';
import TimelineHourglassIcon from '@/components/icons/timeline-shapes/hourglass';
import TimelinePentagonIcon from '@/components/icons/timeline-shapes/pentagon';
import TimelineSquareIcon from '@/components/icons/timeline-shapes/square';
import TimelineStarIcon from '@/components/icons/timeline-shapes/star';
import TimelineTriangleIcon from '@/components/icons/timeline-shapes/triangle';
import TimelineTriangleDownIcon from '@/components/icons/timeline-shapes/triangle-down';
import CreateScheduleRecordDrawer from '@/components/schedule-forms/create-schedule-record-drawer';
import ViewScheduleRecordDrawer from '@/components/schedule-forms/view-schedule-record-drawer';
import { SingleRecordPdfControls } from '@/components/single-record-pdf-controls';
import { TagBadges } from '@/components/table-page/columns/tag-badges';
import { AsyncSelect, SELECT_STYLES } from '@/components/ui/async-select';
import { Badge } from '@/components/ui/badge';
import { inputVariants } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreatableMultiSelect } from '@/components/ui/multi-select';
import { NativeDatePicker } from '@/components/ui/native-date-picker';
import { ReadOnlyField } from '@/components/ui/read-only-field';
import { Select, SELECT_COMPONENTS } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetPortal, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import { useSaveChangesGuard } from '@/hooks/use-save-changes-guard';
import { loadUserOptions } from '@/lib/user-options';
import { cn, initials } from '@/lib/utils';
import { getTagsQueryOptions } from '@/queries/assets';
import { checkKeys, getCheckActionsInfiniteQueryOptions, getCheckByIdQueryOptions } from '@/queries/checks';
import { getScheduleByIdQueryOptions } from '@/queries/schedule';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { useIsAWA } from '@/utils/awa';
import { getWorkspaceRoute } from '@/utils/routes';

import { ActivityTimeline } from '../activity/activity-timeline';
import AttachmentIcon from '../icons/attachment';
import CalendarBlankIcon from '../icons/calendar-blank';
import { EllipsisHorizontalIcon } from '../icons/ellipsis-horizontal';
import FormChecklistIcon from '../icons/form-checklist';
import { ShareIcon } from '../icons/share';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';

interface CheckDrawerProps {
  checkId: string | null;
  open: boolean;
  onOpenChange: (open: boolean, success?: boolean) => void;
  parentWorkOrderId?: string | null;
  onCloseSuccess?: (check: GetCheckResponseDTO) => void;
}

// --- Schedule Check infinite dropdown types and helpers ---

const TIMELINE_SHAPES = [
  TimelineSquareIcon,
  TimelineCircleIcon,
  TimelineTriangleIcon,
  TimelineDiamondIcon,
  TimelineHourglassIcon,
  TimelinePentagonIcon,
  TimelineFidgetSpinnerIcon,
  TimelineHexagonIcon,
  TimelineTriangleDownIcon,
  TimelineStarIcon,
] as const;

function getTimelineShapeIcon(index: number) {
  const safeIndex = index < 0 ? 0 : index % TIMELINE_SHAPES.length;
  return TIMELINE_SHAPES[safeIndex];
}

type ScheduleCheckOption = {
  label: string;
  value: { scheduleCycleId: string; occurrenceNumber: number };
  checkIndex: number; // index into the schedule's checks array (for shape icon)
  occurrenceNumber: number;
  formId: string | null;
};

const ITEMS_PER_PAGE = 20;

/**
 * Generates the flat list of schedule check options by repeating the cycle pattern.
 * Each item in a cycle maps to a schedule check with trigger-based labels.
 *
 * @param schedule - The schedule data from GetScheduleById
 * @param count - Total number of items to generate
 * @returns Array of ScheduleCheckOption
 */
function generateScheduleCheckOptions(
  schedule: GetScheduleByIdResponseDTO['schedule'],
  count: number,
  userTimezone: string | null | undefined,
): ScheduleCheckOption[] {
  const cycles = schedule.cycles ?? [];
  const checks = schedule.checks ?? [];
  const triggers = schedule.triggers ?? [];

  if (cycles.length === 0 || checks.length === 0) return [];

  const options: ScheduleCheckOption[] = [];

  for (let position = 0; position < count; position++) {
    const cycleIndex = position % cycles.length;
    const cycle = cycles[cycleIndex];
    const scheduleCheck = checks.find((c) => c.id === cycle.scheduleCheckId);
    if (!scheduleCheck) continue;

    const checkIndex = checks.findIndex((c) => c.id === cycle.scheduleCheckId);
    const multiplier = position + 1; // 1-indexed multiplier for trigger calculations
    const occurrenceNumber = multiplier;

    // Build trigger display strings (same algorithm as schedule editor preview)
    const triggerDisplays = triggers
      .map((trigger) => {
        if (trigger.calendarInterval) {
          // Calendar-based trigger
          const parsed = parseCalendarInterval(trigger.calendarInterval);
          if (!parsed) return trigger.calendarInterval;

          if (trigger.anchorType === AnchorType.Crawling) {
            return `${parsed.value} ${parsed.unit}`;
          }

          // Fixed calendar: show dates based on baseDate (like schedule editor)
          if (trigger.baseDate) {
            const baseDate = new Date(trigger.baseDate);
            const targetDate = resolveFixedCalendarDueDate(baseDate, parsed.value, parsed.unit, multiplier - 1, trigger.fixedTriggerType);
            return formatDateWithUserTimezone(targetDate, userTimezone);
          }
          return `${(parsed.value * multiplier).toLocaleString()} ${parsed.unit}`;
        }

        if (trigger.unitInterval != null && trigger.unit) {
          // Unit-based trigger: always cumulative (both fixed and crawling)
          const abbreviation = trigger.unit.abbreviation;
          const cumulativeValue = trigger.unitInterval * multiplier;
          return `${cumulativeValue.toLocaleString()} ${abbreviation}`;
        }

        return '';
      })
      .filter(Boolean);

    const triggerLabel = triggerDisplays.length > 0 ? triggerDisplays.join(' or ') : '';
    const label = triggerLabel ? `${scheduleCheck.label} - ${triggerLabel}` : scheduleCheck.label;

    options.push({
      label,
      value: { scheduleCycleId: cycle.id, occurrenceNumber },
      checkIndex,
      occurrenceNumber,
      formId: scheduleCheck.formId ?? null,
    });
  }

  return options;
}

/**
 * Calculates the minimum number of items needed to include the given occurrence number.
 */
function getMinItemsForOccurrence(occurrenceNumber: number): number {
  return Math.max(ITEMS_PER_PAGE, Math.ceil(occurrenceNumber / ITEMS_PER_PAGE) * ITEMS_PER_PAGE);
}

// Custom Option component with shape icon
function ScheduleCheckOptionComponent({ children, ...props }: OptionProps<ScheduleCheckOption, false>) {
  const ShapeIcon = getTimelineShapeIcon(props.data.checkIndex);
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <ShapeIcon size={16} className="shrink-0" />
        <span className="truncate">{children}</span>
      </div>
    </components.Option>
  );
}

// Custom SingleValue component with shape icon
function ScheduleCheckSingleValue(props: SingleValueProps<ScheduleCheckOption, false>) {
  const ShapeIcon = getTimelineShapeIcon(props.data.checkIndex);
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <ShapeIcon size={16} className="shrink-0" />
        <span className="truncate">{props.children}</span>
      </div>
    </components.SingleValue>
  );
}

const checkFormSchema = z
  .object({
    status: z.nativeEnum(CheckStatus),
    completedAt: z.date().nullable(),
    completedReadings: z.record(z.string(), z.number().nullable()),
    completedByUser: z.object({ label: z.string(), value: z.string(), avatarUrl: z.string().optional() }).nullable(),
    scheduleCycleId: z.string().nullable(),
    occurrenceNumber: z.number(),
    tags: z.array(z.object({ label: z.string(), value: z.string() })),
  })
  .superRefine((data, ctx) => {
    if (data.status === CheckStatus.Closed && !data.scheduleCycleId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduleCycleId'],
        message: 'Schedule Check is required to close this check',
      });
    }
  });

type CheckFormValues = z.infer<typeof checkFormSchema>;

export default function CheckDrawer({ checkId, open: openProp, onOpenChange, parentWorkOrderId, onCloseSuccess }: CheckDrawerProps) {
  const [open, setOpen] = useState(openProp);
  const userTimezone = useUserStore.getState().user?.timezone;
  const [isCreateScheduleRecordOpen, setIsCreateScheduleRecordOpen] = useState(false);
  const [isViewScheduleRecordOpen, setIsViewScheduleRecordOpen] = useState(false);
  const [shouldAutoTriggerSharePdf, setShouldAutoTriggerSharePdf] = useState(false);
  const [generatedCount, setGeneratedCount] = useState(ITEMS_PER_PAGE);
  const [isSubmissionDrawerOpen, setIsSubmissionDrawerOpen] = useState(false);
  // Refs (not state) so callbacks that run right after a save — e.g. closeDrawer
  // resuming after the save-changes dialog — read fresh values instead of a stale closure.
  const hasScheduleRecordSuccessRef = useRef(false);
  const hasCheckUpdateSuccessRef = useRef(false);
  const [isFabMenuOpen, setIsFabMenuOpen] = useState(false);
  const [view, setView] = useState<'form' | 'activity'>('form');
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isAWA } = useIsAWA();
  const { isAdmin } = usePermissions();

  useEffect(() => {
    setOpen(openProp);
  }, [openProp]);

  // Fetch check detail
  // Note: enabled depends only on checkId (not open) to preserve data during nested drawer transitions in AWA
  const { data: check, isFetching } = useQuery({
    ...getCheckByIdQueryOptions({ checkId, isAWA }),
    enabled: !!checkId,
  });

  // Fetch schedule data for the schedule check dropdown
  // Note: enabled depends only on scheduleId (not open) to preserve data during nested drawer transitions in AWA
  const scheduleId = check?.scheduleId;
  const { data: scheduleData } = useQuery({
    ...getScheduleByIdQueryOptions({ scheduleId, isAWA }),
    enabled: !!scheduleId,
  });

  const {
    data: checkActionPages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(getCheckActionsInfiniteQueryOptions({ checkId: check?.id, isAWA }));
  const checkActions = checkActionPages?.pages.flatMap((page) => page.data) ?? [];

  const form = useForm<CheckFormValues>({
    resolver: zodResolver(checkFormSchema),
    defaultValues: {
      status: CheckStatus.Open,
      completedAt: null,
      completedReadings: {},
      completedByUser: null,
      scheduleCycleId: null,
      occurrenceNumber: 1,
      tags: [],
    },
  });

  const { data: scheduleTags } = useQuery(getTagsQueryOptions({ entity: TagEntity.Check, isAWA }));
  const scheduleTagOptions = useMemo(() => scheduleTags?.map((tag) => ({ label: tag.tag, value: tag.id })) ?? [], [scheduleTags]);

  const { mutateAsync: updateCheck, isPending: isUpdating } = useMutation({
    mutationFn: async (body: Omit<UpdateCheckRequestDTO, 'checkId'>) => {
      if (!checkId) throw new Error('Check ID is required');
      await ApiService.UpdateCheck({ checkId, ...body });
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: checkKeys.detail(checkId, isAWA) });
      // Exclude this check's detail from the reset — it was just refetched, and
      // resetting it clears `check` to undefined mid-render, unmounting the drawer
      // body and any nested record drawer.
      await queryClient.resetQueries({
        queryKey: checkKeys.all,
        predicate: (query) => !(query.queryKey[1] === 'detail' && query.queryKey[2] === checkId),
      });
      hasCheckUpdateSuccessRef.current = true;
      toast.success('Schedule check updated successfully');
    },
    onError: async (error) => {
      const fallback = 'Unable to update check. Please try again.';
      if (error instanceof HTTPError) {
        try {
          const parsedError = await error.response.json<{ message?: string | string[] }>();
          const message = Array.isArray(parsedError?.message) ? parsedError.message.join(', ') : parsedError?.message;
          toast.error(message || fallback);
        } catch {
          toast.error(fallback);
        }
      } else {
        toast.error(fallback);
      }
    },
  });

  // Reset success state when switching to a different check
  useEffect(() => {
    hasScheduleRecordSuccessRef.current = false;
    hasCheckUpdateSuccessRef.current = false;
    setIsSubmissionDrawerOpen(false);
  }, [checkId]);

  // When check loads, ensure we generate enough items to include the current occurrence
  useEffect(() => {
    if (check?.occurrenceNumber) {
      const minItems = getMinItemsForOccurrence(check.occurrenceNumber);
      setGeneratedCount((prev) => Math.max(prev, minItems));
    }
  }, [check?.occurrenceNumber]);

  // Generate the infinite list of schedule check options
  const scheduleCheckOptions = useMemo(() => {
    if (!scheduleData) return [];
    return generateScheduleCheckOptions(scheduleData, generatedCount, userTimezone);
  }, [scheduleData, generatedCount, userTimezone]);

  // Load more items when scrolling to bottom
  const handleLoadMore = useCallback(() => {
    setGeneratedCount((prev) => prev + ITEMS_PER_PAGE);
  }, []);

  // Reset form when check or schedule data loads/changes
  useEffect(() => {
    if (!check) return;

    const isOpen = check.status === CheckStatus.Open;
    const sourceCompletedAt = isOpen ? check.draftCompletedAt : check.completedAt;
    const sourceCompletedReadings = isOpen ? check.draftCompletedReadings : check.completedReadings;

    const readingsByUnitId: Record<string, number | null> = {};
    for (const trigger of scheduleData?.triggers ?? []) {
      const { unit } = trigger;
      if (!unit) continue;
      const existing = sourceCompletedReadings?.find((r) => r.unitName === unit.name);
      readingsByUnitId[unit.id] = existing ? parseFloat(existing.reading) : null;
    }

    form.reset({
      status: check.status,
      completedAt: sourceCompletedAt ? new Date(sourceCompletedAt) : null,
      completedReadings: readingsByUnitId,
      completedByUser: check.completedByUser
        ? {
            value: check.completedByUser.id,
            label: EntityRefUtil.createUserFullNameRef(check.completedByUser),
            avatarUrl: check.completedByUser.avatarUrl ?? undefined,
          }
        : null,
      scheduleCycleId: check.scheduleCycleId ?? null,
      occurrenceNumber: check.occurrenceNumber,
      tags: check.tags.map((tag) => ({ label: tag.tag, value: tag.id })),
    });
  }, [check, scheduleData, form]);

  // Resolve the currently-selected ScheduleCheckOption for the picker from form state
  const formScheduleCycleId = form.watch('scheduleCycleId');
  const formOccurrenceNumber = form.watch('occurrenceNumber');
  const selectedOption = useMemo(() => {
    return scheduleCheckOptions.find((opt) => opt.value.scheduleCycleId === formScheduleCycleId && opt.value.occurrenceNumber === formOccurrenceNumber) ?? null;
  }, [scheduleCheckOptions, formScheduleCycleId, formOccurrenceNumber]);

  async function onSubmit(values: CheckFormValues) {
    if (!checkId) return;

    if (completedByRequired && !values.completedByUser) {
      form.setError('completedByUser', { type: 'manual', message: 'Required' });
      return;
    }

    const completedReadings = Object.entries(values.completedReadings)
      .filter(([, reading]) => reading !== null && reading !== undefined)
      .map(([unitId, reading]) => ({ unitId, reading: String(reading) }));

    const wasOpenBeforeSave = check?.status === CheckStatus.Open;

    const tags = values.tags.map((tag) => tag.label);

    if (check?.status === CheckStatus.Closed) {
      // Editing a latest, manually-completed check: completion date, readings, completed-by, and tags are mutable.
      await updateCheck({
        ...(values.completedAt ? { completedAt: values.completedAt.toISOString() } : {}),
        completedReadings,
        completedByUserId: values.completedByUser?.value ?? null,
        tags,
      });
    } else {
      await updateCheck({
        status: values.status,
        ...(values.completedAt ? { completedAt: values.completedAt.toISOString() } : {}),
        ...(values.scheduleCycleId ? { scheduleCycleId: values.scheduleCycleId } : {}),
        occurrenceNumber: values.occurrenceNumber,
        completedReadings,
        completedByUserId: values.completedByUser?.value ?? null,
        tags,
      });
    }

    form.reset(values);

    if (wasOpenBeforeSave && values.status === CheckStatus.Closed && check) {
      onCloseSuccess?.(check);
    }
  }

  const { isConfirmOpen, confirmSafeToLeave, settleConfirm, onConfirmOpenChange } = useSaveChangesGuard(() => form.formState.isDirty);

  function notifyCloseToParent() {
    const success = hasScheduleRecordSuccessRef.current || hasCheckUpdateSuccessRef.current;
    hasScheduleRecordSuccessRef.current = false;
    hasCheckUpdateSuccessRef.current = false;
    onOpenChange(false, success);
  }

  async function closeDrawer() {
    if (!(await confirmSafeToLeave())) return;
    setView('form');
    notifyCloseToParent();
  }

  const statusBadgeVariant = match(check?.status)
    .with(CheckStatus.Open, () => 'grey' as const)
    .with(CheckStatus.Closed, () => 'success' as const)
    .otherwise(() => 'grey' as const);

  const statusText = match(check?.status)
    .with(CheckStatus.Open, () => 'Open')
    .with(CheckStatus.Closed, () => 'Closed')
    .otherwise(() => check?.status ?? '-');

  const statusOptions = useMemo<{ label: ReactNode; value: CheckStatus }[]>(
    () => [
      {
        value: CheckStatus.Open,
        label: (
          <Badge className="text-xs" variant="grey">
            Open
          </Badge>
        ),
      },
      {
        value: CheckStatus.Closed,
        label: (
          <Badge className="text-xs" variant="success">
            Closed
          </Badge>
        ),
      },
    ],
    [],
  );

  const scheduleRecordFormId = selectedOption?.formId ?? check?.scheduleCheck?.formId ?? null;

  const completedByName = EntityRefUtil.createUserFullNameRef(check?.completedByUser) || null;

  const isClosed = check?.status === CheckStatus.Closed;
  const isFormStatusClosed = form.watch('status') === CheckStatus.Closed;

  // A manually-completed check (no linked schedule form record) that is the latest in its series
  // keeps its completion date + completed readings editable; older or record-linked closed checks
  // stay read-only (record-linked ones are edited on the record). Non-AWA; Member + Admin.
  const isEditableClosedCheck = isClosed && !!check?.isLatestClosed && !check?.scheduleRecord;
  const canEditCompletion = !isAWA && (!isClosed || isEditableClosedCheck);
  // A closed check must keep a completer; on an open check completed-by is optional.
  const completedByRequired = isClosed;

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(newOpen) => {
          if (!newOpen && (isCreateScheduleRecordOpen || isUpdating)) return;
          if (newOpen) {
            onOpenChange(true);
            return;
          }
          closeDrawer();
        }}>
        <SheetPortal>
          <SheetContent
            hideCloseIcon
            side="right"
            className={cn(isAWA ? 'mx-auto h-full w-full focus:outline-none sm:max-w-[800px]' : 'focus:outline-none', isFabMenuOpen && 'border-l-0')}
            onEscapeKeyDown={(e) => {
              if (isUpdating) e.preventDefault();
            }}>
            <form
              className="flex h-full flex-1 flex-col"
              onSubmit={(e) => {
                e.stopPropagation();
                form.handleSubmit(onSubmit)(e);
              }}>
              <SheetHeader
                className={cn(
                  isAWA ? 'border-grey-3 m-0 border-b-2 p-0' : 'border-grey-3 h-[64px] flex-row items-center justify-between space-y-0 border-b bg-white px-6',
                )}>
                {isAWA ? (
                  <div className="flex items-center justify-between bg-white px-4 py-[25px]">
                    <div className="flex items-center gap-2">
                      <button type="button" aria-label="Go back" onClick={closeDrawer}>
                        <ArrowLeftIcon size={30} />
                      </button>
                      <div className="line-clamp-1 text-xl font-medium">{isFetching ? 'Loading check details...' : 'View schedule check record'}</div>
                    </div>
                  </div>
                ) : (
                  <>
                    <SheetTitle>{isFetching ? 'Loading check details...' : 'Schedule check record'}</SheetTitle>
                    <div className="flex items-center gap-2">
                      {check && (
                        <Button variant="ghost" size="sm" type="button" onClick={() => setView((prev) => (prev === 'activity' ? 'form' : 'activity'))}>
                          <ActivityIcon className="h-4 w-4" />
                          {view === 'activity' ? 'Hide Activity' : 'Show Activity'}
                        </Button>
                      )}
                      {check && <SingleRecordPdfControls recordType="check" recordId={check.id} documentUrl={undefined} />}
                      {check && (!isClosed || isEditableClosedCheck) && (
                        <Button variant="success" type="submit" disabled={!form.formState.isDirty || isUpdating}>
                          Save
                        </Button>
                      )}
                      <button
                        type="button"
                        className="data-[state=open]:bg-default-blue cursor-pointer rounded-sm opacity-70 transition-opacity outline-none hover:opacity-100 disabled:pointer-events-none"
                        disabled={isUpdating}
                        onClick={closeDrawer}>
                        <CloseIcon className="h-4 w-4" />
                        <span className="sr-only">Close</span>
                      </button>
                    </div>
                  </>
                )}
              </SheetHeader>

              {isFetching ? (
                <CheckDrawerSkeleton />
              ) : check && view === 'activity' ? (
                <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-4">
                  <div>Activity</div>
                  <div className="border-b" />
                  <ActivityTimeline actions={checkActions} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} />
                </div>
              ) : check ? (
                <div className="scrollbar scrollbar-track-transparent scrollbar-thumb-grey-3 flex flex-1 flex-col overflow-y-auto">
                  {/* ID Header */}
                  <div className="flex flex-col gap-1 px-6 pt-4">
                    <div className="flex flex-col gap-[2px]">
                      <div className="text-sm">
                        <span className="font-medium">ID: </span>
                        {EntityRefUtil.createCheckRef(check.number)}
                      </div>
                      <div className="text-secondary-text text-sm">
                        Created: {formatManuallyWithUserTimezone(new Date(check.createdAt), 'MMM DD, YYYY hh:mm a', userTimezone)}
                      </div>
                    </div>
                    <div className="border-default-blue border-b-4" />
                  </div>

                  <div className="flex flex-col gap-2 px-6 pt-4 pb-6">
                    {/* Asset */}
                    <ReadOnlyField label="Asset">
                      {isAWA ? (
                        <span className="text-sm">{EntityRefUtil.createAssetLabel(check.asset)}</span>
                      ) : (
                        <Link
                          className="text-default-blue text-sm underline"
                          to={getWorkspaceRoute(`/assets/${check.assetId}`)}
                          onClick={async (e) => {
                            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                            e.preventDefault();
                            if (!(await confirmSafeToLeave())) return;
                            navigate(getWorkspaceRoute(`/assets/${check.assetId}`));
                          }}>
                          {EntityRefUtil.createAssetLabel(check.asset)}
                        </Link>
                      )}
                    </ReadOnlyField>

                    {/* Status */}
                    {!isAWA && !isClosed ? (
                      <div className="flex flex-col gap-1">
                        <Label>Status</Label>
                        <Controller
                          control={form.control}
                          name="status"
                          render={({ field: { value, onChange, onBlur } }) => (
                            <Select<{ label: ReactNode; value: CheckStatus }>
                              options={statusOptions}
                              value={statusOptions.find((option) => option.value === value)}
                              onChange={(option) => option && onChange(option.value)}
                              onBlur={onBlur}
                              isSearchable={false}
                            />
                          )}
                        />
                      </div>
                    ) : (
                      <ReadOnlyField label="Status">
                        <Badge size="sm" variant={statusBadgeVariant}>
                          {statusText}
                        </Badge>
                      </ReadOnlyField>
                    )}

                    {/* Schedule */}
                    <ReadOnlyField label="Schedule">
                      {isAWA ? (
                        <span className="text-sm">{check.schedule.name}</span>
                      ) : (
                        <Link
                          className="text-default-blue text-sm underline"
                          to={getWorkspaceRoute(`/schedules/${check.scheduleId}`)}
                          onClick={async (e) => {
                            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                            e.preventDefault();
                            if (!(await confirmSafeToLeave())) return;
                            navigate(getWorkspaceRoute(`/schedules/${check.scheduleId}`));
                          }}>
                          {check.schedule.name}
                        </Link>
                      )}
                    </ReadOnlyField>

                    {/* Schedule Check - Infinite scroll dropdown */}
                    <div className="flex flex-col gap-1">
                      <Label>Schedule Check</Label>
                      <Select<ScheduleCheckOption>
                        options={scheduleCheckOptions}
                        value={selectedOption}
                        isDisabled={isAWA || isClosed || !isAdmin}
                        error={!!form.formState.errors.scheduleCycleId}
                        onChange={(option) => {
                          if (option) {
                            form.setValue('scheduleCycleId', option.value.scheduleCycleId, { shouldDirty: true, shouldValidate: true });
                            form.setValue('occurrenceNumber', option.value.occurrenceNumber, { shouldDirty: true });
                          }
                        }}
                        getOptionValue={(opt) => `${opt.value.scheduleCycleId}-${opt.value.occurrenceNumber}`}
                        formatOptionLabel={(option) => {
                          const parts = option.label.split(' or ');
                          if (parts.length <= 1) return option.label;
                          return (
                            <>
                              {parts.map((part, i) => (
                                <Fragment key={i}>
                                  {i > 0 && <span className="text-secondary-text"> or </span>}
                                  {part}
                                </Fragment>
                              ))}
                            </>
                          );
                        }}
                        placeholder="Select Schedule Check..."
                        isSearchable={false}
                        onMenuScrollToBottom={handleLoadMore}
                        components={{
                          ...SELECT_COMPONENTS<ScheduleCheckOption>(),
                          Option: ScheduleCheckOptionComponent,
                          SingleValue: ScheduleCheckSingleValue,
                        }}
                      />
                      {form.formState.errors.scheduleCycleId && <p className="text-default-red text-sm">{form.formState.errors.scheduleCycleId.message}</p>}
                    </div>

                    {/* Work Order */}
                    <div className="flex flex-col gap-1">
                      {isAWA ? (
                        <>
                          <Label>Work Order</Label>
                          <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                            {check.workOrder ? (
                              <div className="text-sm">{EntityRefUtil.createWorkOrderRef(check.workOrder.number)}</div>
                            ) : (
                              <div className="text-secondary-text text-sm">-</div>
                            )}
                          </div>
                        </>
                      ) : (
                        <ReadOnlyField label="Work Order">
                          {check.workOrder ? (
                            <button
                              type="button"
                              className="text-default-blue cursor-pointer text-sm underline"
                              onClick={async () => {
                                const workOrder = check.workOrder;
                                if (!workOrder) return;
                                if (!(await confirmSafeToLeave())) return;
                                if (parentWorkOrderId && parentWorkOrderId === workOrder.id) {
                                  notifyCloseToParent();
                                  return;
                                }
                                try {
                                  const workOrderData = (await (await ApiService.GetWorkOrderById({ workOrderId: workOrder.id })).json()).workOrder;
                                  navigate(getWorkspaceRoute('/work-orders'), { state: { workOrder: workOrderData } });
                                } catch (error) {
                                  console.error('Error fetching work order:', error);
                                  toast.error('Failed to load work order');
                                }
                              }}>
                              {EntityRefUtil.createWorkOrderRef(check.workOrder.number)}
                            </button>
                          ) : (
                            <span className="text-secondary-text text-sm">-</span>
                          )}
                        </ReadOnlyField>
                      )}

                      <Button
                        type="button"
                        className="w-fit"
                        variant="default"
                        icon={<CalendarBlankIcon />}
                        disabled={isAWA || isClosed || !!check.workOrder}
                        onClick={async () => {
                          if (!(await confirmSafeToLeave())) return;
                          navigate(getWorkspaceRoute('/work-orders'), { state: { check } });
                        }}>
                        Create Work Order
                      </Button>
                    </div>

                    {/* Schedule Form */}
                    <ReadOnlyField label="Schedule Form">
                      {check.scheduleForm ? (
                        isAWA ? (
                          <span className="text-sm">{check.scheduleForm.name}</span>
                        ) : (
                          (() => {
                            const scheduleForm = check.scheduleForm;
                            const scheduleFormRoute = getWorkspaceRoute(`/schedules/forms/${scheduleForm.id}/submissions`);
                            return (
                              <Link
                                className="text-default-blue text-sm underline"
                                to={scheduleFormRoute}
                                onClick={async (e) => {
                                  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                                  e.preventDefault();
                                  if (!(await confirmSafeToLeave())) return;
                                  navigate(scheduleFormRoute);
                                }}>
                                {scheduleForm.name}
                              </Link>
                            );
                          })()
                        )
                      ) : (
                        <span className="text-sm">-</span>
                      )}
                    </ReadOnlyField>

                    {/* Schedule Form Record */}
                    <div className="flex flex-col gap-1">
                      <ReadOnlyField label="Schedule Form Record">
                        {check.scheduleRecord ? (
                          <button
                            type="button"
                            className="text-default-blue cursor-pointer text-sm underline"
                            onClick={async () => {
                              if (!(await confirmSafeToLeave())) return;
                              setOpen(false);
                              setIsViewScheduleRecordOpen(true);
                            }}>
                            {EntityRefUtil.createScheduleFormSubmissionRef(check.scheduleRecord.number)}
                          </button>
                        ) : (
                          <span className="text-secondary-text text-sm">-</span>
                        )}
                      </ReadOnlyField>

                      <Button
                        type="button"
                        className="w-fit"
                        variant="default"
                        icon={<AttachmentIcon />}
                        disabled={isClosed || isFormStatusClosed || !!check.scheduleRecord || !scheduleRecordFormId}
                        onClick={async () => {
                          if (!(await confirmSafeToLeave())) return;
                          setOpen(false);
                          setIsCreateScheduleRecordOpen(true);
                        }}>
                        Create Schedule Form Record
                      </Button>
                    </div>

                    {/* Due Progress */}
                    <DueProgressField check={check} anchorType={scheduleData?.triggers?.[0]?.anchorType} />

                    {/* Date Remaining - one row per calendar unit from summary */}
                    {(['d', 'w', 'm'] as const)
                      .filter((unit) =>
                        (scheduleData?.triggers ?? []).some((trigger) => {
                          if (!trigger.calendarInterval) return false;
                          const parsed = parseCalendarInterval(trigger.calendarInterval);
                          return parsed?.unit === unit;
                        }),
                      )
                      .map((calendarUnit) => {
                        const label = match(calendarUnit)
                          .with('d', () => 'Days Remaining')
                          .with('w', () => 'Weeks Remaining')
                          .with('m', () => 'Months Remaining')
                          .exhaustive();
                        const dateProgress = check.allDateProgress?.find((d) => d.calendarUnit === calendarUnit);
                        return (
                          <ReadOnlyField key={calendarUnit} label={label}>
                            {dateProgress ? (
                              <span className="text-sm">{`${dateProgress.days} d`}</span>
                            ) : (
                              <span className="text-secondary-text text-sm">-</span>
                            )}
                          </ReadOnlyField>
                        );
                      })}

                    {/* Unit Remaining - one row per unit from summary */}
                    {check.summary
                      .filter((row) => !['Days', 'Weeks', 'Months'].includes(row.unit))
                      .map((row) => {
                        const unitProgress = check.allUnitProgress?.find((u) => u.unitName === row.unit);
                        return (
                          <ReadOnlyField key={row.unit} label={`${row.unit} Remaining`}>
                            {unitProgress ? (
                              <span className="text-sm">{`${unitProgress.remaining.toLocaleString()} ${unitProgress.unitAbbreviation}`}</span>
                            ) : (
                              <span className="text-secondary-text text-sm">-</span>
                            )}
                          </ReadOnlyField>
                        );
                      })}

                    {/* Summary Table - ensure calendar rows appear when schedule has calendar triggers */}
                    {(() => {
                      const existingUnits = new Set(check.summary.map((row) => row.unit));

                      // Add missing calendar rows based on actual triggers
                      const missingCalendarRows = (scheduleData?.triggers ?? [])
                        .filter((trigger) => trigger.calendarInterval)
                        .map((trigger) => {
                          const parsed = parseCalendarInterval(trigger.calendarInterval!);
                          if (!parsed) return null;
                          const unit = match(parsed.unit)
                            .with('d', () => 'Days')
                            .with('w', () => 'Weeks')
                            .with('m', () => 'Months')
                            .exhaustive();
                          return unit;
                        })
                        .filter((unit): unit is string => unit != null && !existingUnits.has(unit))
                        .map((unit) => ({
                          unit,
                          unitAbbreviation: null,
                          lastCheckReading: null,
                          currentAssetReading: null,
                          dueCheckReading: null,
                          lastCheckDate: null,
                          currentDate: null,
                          dueDate: null,
                        }));

                      const summaryRows = [...check.summary, ...missingCalendarRows];

                      if (summaryRows.length === 0) return null;

                      return (
                        <div className="flex flex-col gap-1">
                          <Label className="">Summary</Label>
                          <div className="border-grey-3 overflow-x-auto overflow-y-hidden rounded-md border">
                            <Table>
                              <TableHeader className={cn('[&_tr]:border-0', isClosed && 'bg-grey-2')}>
                                <TableRow>
                                  <TableHead className="border-grey-3 border-0 border-b-2">Unit</TableHead>
                                  <TableHead className="border-grey-3 border-0 border-b-2">Last Schedule Check</TableHead>
                                  <TableHead className="border-grey-3 border-0 border-b-2">Last Reading</TableHead>
                                  <TableHead className="border-grey-3 border-0 border-b-2">Due Schedule Check</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {summaryRows.map((row, index) => (
                                  <TableRow
                                    key={row.unit}
                                    className={cn(index < summaryRows.length - 1 ? 'border-grey-3 border-b' : 'border-0', isClosed && 'bg-grey-1')}>
                                    <TableCell className="border-0 py-2">{row.unit}</TableCell>
                                    <TableCell className="border-0 py-2">
                                      {row.lastCheckReading ??
                                        (row.lastCheckDate ? formatDateWithUserTimezone(new Date(row.lastCheckDate), userTimezone) : '-')}
                                    </TableCell>
                                    <TableCell className="border-0 py-2">
                                      {row.currentAssetReading ?? (row.currentDate ? formatDateWithUserTimezone(new Date(row.currentDate), userTimezone) : '-')}
                                    </TableCell>
                                    <TableCell className="border-0 py-2">
                                      {row.dueCheckReading ?? (row.dueDate ? formatDateWithUserTimezone(new Date(row.dueDate), userTimezone) : '-')}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Completed Date */}
                    {canEditCompletion ? (
                      <div className="flex flex-col gap-1">
                        <Label>Completed Date</Label>
                        <Controller
                          control={form.control}
                          name="completedAt"
                          render={({ field: { value, onChange, onBlur } }) => (
                            <NativeDatePicker type="date" value={value} onChange={onChange} onBlur={onBlur} />
                          )}
                        />
                      </div>
                    ) : (
                      <ReadOnlyField label="Completed Date">
                        <span className="text-sm">
                          {check.completedAt ? formatManuallyWithUserTimezone(new Date(check.completedAt), 'DD/MM/YYYY', userTimezone) : '-'}
                        </span>
                      </ReadOnlyField>
                    )}

                    {/* Completed unit readings - one field per trigger unit */}
                    {(scheduleData?.triggers ?? []).map((trigger) => {
                      const { unit } = trigger;
                      if (!unit) return null;
                      const { id: unitId, name: unitName, abbreviation: unitAbbreviation } = unit;

                      if (canEditCompletion) {
                        return (
                          <div key={trigger.id} className="flex flex-col gap-1">
                            <Label>{`Completed ${unitName}`}</Label>
                            <Controller
                              control={form.control}
                              name={`completedReadings.${unitId}`}
                              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                                <NumericFormat
                                  className={cn(inputVariants({ error: !!error }), 'disabled:bg-grey-1')}
                                  value={value ?? ''}
                                  onValueChange={(values) => onChange(values.floatValue ?? null)}
                                  onBlur={onBlur}
                                  isAllowed={({ floatValue }) => floatValue === undefined || floatValue <= 10000000}
                                  placeholder="Enter number..."
                                  allowNegative={false}
                                  thousandSeparator=","
                                  decimalScale={2}
                                  suffix={` ${unitAbbreviation}`}
                                  inputMode="decimal"
                                />
                              )}
                            />
                          </div>
                        );
                      }

                      const reading = check.completedReadings?.find((r) => r.unitName === unitName);
                      return (
                        <ReadOnlyField key={trigger.id} label={`Completed ${unitName}`}>
                          <span className="text-sm">{reading ? `${reading.reading} ${reading.unitAbbreviation}` : '-'}</span>
                        </ReadOnlyField>
                      );
                    })}

                    {/* Completed By */}
                    {canEditCompletion ? (
                      <div className="flex flex-col gap-1">
                        <Label required={completedByRequired}>Completed By</Label>
                        <Controller
                          control={form.control}
                          name="completedByUser"
                          render={({ field: { value, onChange }, fieldState: { error } }) => (
                            <>
                              <AsyncSelect<{ label: string; value: string; avatarUrl?: string }>
                                defaultOptions
                                menuPlacement="auto"
                                classNames={SELECT_STYLES(!!error)}
                                value={value ?? null}
                                onChange={(option) => {
                                  onChange(option ?? null);
                                  form.clearErrors('completedByUser');
                                }}
                                loadOptions={(input) => loadUserOptions(input, 'all', () => toast.error('Failed to load users'))}
                                formatOptionLabel={(option) => (
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-5 w-5 text-xs">
                                      {option.avatarUrl ? <AvatarImage src={option.avatarUrl} /> : <AvatarFallback>{initials(option.label)}</AvatarFallback>}
                                    </Avatar>
                                    <span>{option.label}</span>
                                  </div>
                                )}
                                placeholder="Select completed by..."
                              />
                              {error && <div className="text-default-red text-sm">{error.message}</div>}
                            </>
                          )}
                        />
                      </div>
                    ) : (
                      <ReadOnlyField label="Completed By">
                        {completedByName ? (
                          <div className="flex items-center gap-2">
                            <Avatar className="h-7 w-7 text-xs">
                              {check.completedByUser?.avatarUrl ? (
                                <AvatarImage src={check.completedByUser?.avatarUrl || ''} />
                              ) : (
                                <AvatarFallback>{initials(completedByName)}</AvatarFallback>
                              )}
                            </Avatar>
                            <span className="text-sm">{completedByName}</span>
                          </div>
                        ) : (
                          <span className="text-sm">-</span>
                        )}
                      </ReadOnlyField>
                    )}

                    <div className="flex flex-col gap-1">
                      <Label>Schedule Check Tags</Label>
                      <Controller
                        control={form.control}
                        name="tags"
                        render={({ field: { value, onChange } }) => (
                          <CreatableMultiSelect<{ label: string; value: string }>
                            options={scheduleTagOptions}
                            value={value}
                            onChange={(selected) => onChange(selected ? [...selected] : [])}
                            placeholder="Select Tags..."
                            menuPlacement="auto"
                            chipVariant="violet"
                          />
                        )}
                      />
                    </div>

                    <ReadOnlyField label="Asset Tags">
                      <TagBadges tags={check.asset.tags} />
                    </ReadOnlyField>
                  </div>
                </div>
              ) : null}

              {/* FAB menu overlay */}
              {isAWA && scheduleRecordFormId && isFabMenuOpen && (
                <div
                  className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80"
                  onClick={() => setIsFabMenuOpen(false)}
                />
              )}

              {/* FAB menu for AWA actions */}
              {isAWA && scheduleRecordFormId && (
                <div className="absolute right-4 bottom-4 z-50">
                  <DropdownMenu open={isFabMenuOpen} onOpenChange={setIsFabMenuOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="default"
                        size="icon"
                        className="bg-default-blue hover:bg-default-dark-blue h-[65px] w-[65px] rounded-full shadow-md">
                        {isFabMenuOpen ? <CloseIcon size={30} className="text-white" /> : <EllipsisHorizontalIcon size={30} className="text-white" />}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" side="top" className="mb-5 flex flex-col gap-[20px] border-0 bg-transparent p-0 shadow-none">
                      {/* Share as PDF button */}
                      <DropdownMenuItem
                        asChild
                        disabled={!check?.scheduleRecord}
                        onClick={() => {
                          if (check?.scheduleRecord) {
                            setIsFabMenuOpen(false);
                            setOpen(false);
                            setShouldAutoTriggerSharePdf(true);
                            setIsViewScheduleRecordOpen(true);
                          }
                        }}
                        className="cursor-pointer p-0 hover:bg-transparent focus:bg-transparent data-[disabled]:opacity-100 data-[highlighted]:bg-transparent">
                        <div className="flex w-full items-center justify-end gap-2.5">
                          <div className="text-default-text rounded bg-white px-2.5 py-1 text-base font-medium shadow-md">Share as PDF</div>
                          <div
                            className={cn(
                              'bg-default-blue flex h-[65px] w-[65px] items-center justify-center rounded-full shadow-md',
                              !check?.scheduleRecord && 'opacity-50',
                            )}>
                            <ShareIcon size={30} className="text-white" />
                          </div>
                        </div>
                      </DropdownMenuItem>
                      {/* Create Schedule Form Record button */}
                      <DropdownMenuItem
                        asChild
                        disabled={isClosed || !!check?.scheduleRecord}
                        onClick={() => {
                          if (!isClosed && !check?.scheduleRecord) {
                            setIsFabMenuOpen(false);
                            setOpen(false);
                            setIsCreateScheduleRecordOpen(true);
                          }
                        }}
                        className="cursor-pointer p-0 hover:bg-transparent focus:bg-transparent data-[disabled]:opacity-100 data-[highlighted]:bg-transparent">
                        <div className="flex w-full items-center justify-end gap-2.5">
                          <div className="text-default-text rounded bg-white px-2.5 py-1 text-base font-medium shadow-md">
                            Create {check?.scheduleForm?.name ?? 'Schedule Form Record'}
                          </div>
                          <div
                            className={cn(
                              'bg-default-blue flex h-[65px] w-[65px] items-center justify-center rounded-full shadow-md',
                              (isClosed || !!check?.scheduleRecord) && 'opacity-50',
                            )}>
                            <FormChecklistIcon size={30} className="text-white" />
                          </div>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </form>
          </SheetContent>
        </SheetPortal>
      </Sheet>
      {check && scheduleRecordFormId && (
        <CreateScheduleRecordDrawer
          formId={scheduleRecordFormId}
          assetId={check.assetId}
          checkId={check.id}
          scheduleCheck={selectedOption ? { label: selectedOption.label } : undefined}
          open={isCreateScheduleRecordOpen}
          onOpenChange={(open, success) => {
            setIsCreateScheduleRecordOpen(open);
            if (success) {
              hasScheduleRecordSuccessRef.current = true;
              queryClient.refetchQueries({ queryKey: checkKeys.detail(checkId, isAWA) });
              queryClient.invalidateQueries({ queryKey: checkKeys.all });
            }
            if (!open) {
              if (isAWA && (success || hasScheduleRecordSuccessRef.current)) {
                setIsSubmissionDrawerOpen(true);
              } else {
                setOpen(true);
              }
            }
          }}
        />
      )}
      {check?.scheduleRecord && (
        <ViewScheduleRecordDrawer
          scheduleFormId={check.scheduleRecord.id}
          open={isViewScheduleRecordOpen}
          onOpenChange={(open) => {
            setIsViewScheduleRecordOpen(open);
            if (!open) {
              setOpen(true);
              setShouldAutoTriggerSharePdf(false);
            }
          }}
          noTrigger
          autoTriggerSharePdf={shouldAutoTriggerSharePdf}
        />
      )}
      {isAWA && (
        <AWAFormSubmissionDrawer
          open={isSubmissionDrawerOpen}
          onOpenChange={(drawerOpen) => {
            setIsSubmissionDrawerOpen(drawerOpen);
            if (!drawerOpen) {
              setOpen(true);
            }
          }}
          title="Schedule form submitted"
          description="Go to the home page to continue working or view the completed check record"
          recordLabel="View check record"
          recordIcon={<ClockIcon size={20} />}
        />
      )}
      <SaveChangesDialog
        open={isConfirmOpen}
        onOpenChange={onConfirmOpenChange}
        saveCallback={form.handleSubmit(async (values) => {
          try {
            await onSubmit(values);
            // onSubmit bails without saving when completed-by validation fails — keep
            // the dialog open rather than resuming the pending action unsaved.
            if (form.getFieldState('completedByUser').error) return;
            // Only settle the confirm — the pending action (close, create record,
            // view record, navigate) resumes and decides what happens next. Closing
            // the drawer here would stack its overlay under the resumed action's.
            settleConfirm(true);
          } catch {
            // mutation's onError already surfaced the toast; leave dialog open for retry
          }
        })}
        discardCallback={() => {
          form.reset();
          settleConfirm(true);
        }}
        loading={isUpdating}
        title="Save changes?"
        description="Do you want to save your changes before leaving?"
      />
    </>
  );
}

function DueProgressField({ check, anchorType }: { check: GetCheckResponseDTO; anchorType: AnchorType | undefined }) {
  if (isCheckCompleted(check)) {
    return (
      <div className="flex flex-col gap-1">
        <Label>Due Progress</Label>
        <CheckIcon className="text-default-green h-5 w-5" />
      </div>
    );
  }

  if (check.dueProgress == null && !check.isUnlinked) {
    return <span className="text-secondary-text text-sm">-</span>;
  }

  return (
    <div className="mb-1 flex flex-col gap-1">
      <Label>Due Progress</Label>
      <div className="flex-1">
        <CheckProgressBar
          isClosed={false}
          isUnlinked={check.isUnlinked}
          anchorType={anchorType}
          hasScheduleCycle={check.scheduleCycleId != null}
          dueProgress={check.dueProgress}
          dateRemaining={check.dateRemaining}
          showTooltip
          allUnitProgress={check.allUnitProgress}
          allDateProgress={check.allDateProgress}
        />
      </div>
    </div>
  );
}

function CheckDrawerSkeleton() {
  return (
    <div className="flex flex-col gap-4 px-6 pt-4">
      <div className="flex flex-col gap-1">
        <Skeleton className="h-4 w-24" />
        <div className="border-grey-2 animate-pulse border-b-4" />
      </div>
      {[...Array(8)].map((_, i) => (
        <div key={i} className="flex flex-col gap-1">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-[41px] w-full rounded" />
        </div>
      ))}
      <Skeleton className="mt-2 h-32 w-full rounded" />
    </div>
  );
}
